import { useCallback, useEffect, useState } from "react";
import { Card } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { handleAxiosError } from "app/services/app.error.handler";
import { loadAllDepartments, deleteDepartment } from "app/services/department.service";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { departmentActions } from "store/department-slice";
import { ConfirmDialog } from "../ConfirmDialog";
import AddDepartmentDialog from "./AddDepartmentDialog";
import { DepartmentsDataGrid } from "./DepartmentsDataGrid";

// @since NEXT_VERSION
const AllDepartmentsView = () => {
  const dispatch = useDispatch();
  const departments = useSelector((state) => state.department.rows);

  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editObject, setEditObject] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  // No status filter: the master grid must show disabled departments so they can be re-enabled.
  const refreshDepartments = useCallback(() => {
    setIsLoading(true);
    loadAllDepartments()
      .then((response) =>
        dispatch(departmentActions.replaceData(Array.isArray(response.data) ? response.data : []))
      )
      .catch((error) => {
        dispatch(departmentActions.replaceData([]));
        handleAxiosError(error);
      })
      .finally(() => setIsLoading(false));
  }, [dispatch]);

  useEffect(() => {
    if (departments.length !== 0) return;
    refreshDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departments.length]);

  const editEventHandler = (data) => {
    setEditObject(data);
    setOpen(true);
  };

  const confirmDeleteDialogHandler = (data) => {
    setConfirmationMessage(
      `Disable the department '${data.displayName}' (${data.code})? ` +
        `Users already assigned to it keep the assignment, but it will no longer be selectable for new users.`
    );
    setDeleteId(data.id);
    setConfirmOpen(true);
  };

  const deleteHandler = async () => {
    await deleteDepartment(deleteId)
      .then(() => refreshDepartments())
      .catch((error) => handleAxiosError(error));
  };

  return (
    <div style={{ width: "100%" }}>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={1}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
        >
          <MDTypography variant="h6" color="white">
            Departments
          </MDTypography>
        </MDBox>
        <MDBox height={500} mb={0} sx={{ overflowX: "auto" }}>
          <DepartmentsDataGrid
            departments={departments}
            isLoading={isLoading}
            onAddDepartment={() => {
              setEditObject(null);
              setOpen(true);
            }}
            editEventHandler={editEventHandler}
            confirmDeleteDialogHandler={confirmDeleteDialogHandler}
          />
        </MDBox>
      </Card>
      <AddDepartmentDialog
        open={open}
        setOpen={setOpen}
        refreshDepartmentsHandler={refreshDepartments}
        editObject={editObject}
      />
      <ConfirmDialog
        title={"Disable This Department?"}
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={deleteHandler}
        yesButtonLabel="Disable"
        noButtonLabel="Cancel"
      >
        {confirmationMessage}
      </ConfirmDialog>
    </div>
  );
};

export default AllDepartmentsView;
